import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { contributionService, Contribution, Contributor } from "../../lib/firebase";
import { Dialog } from "../../components/ui/dialog";

const ADMIN_EMAIL = "admin@rawpa.com";

function getInitials(name: string) {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function getRandomAnon() {
  return `Anonymous-${Math.floor(10000 + Math.random() * 90000)}`;
}
function getProfileImg(contributor: Contributor) {
  if (contributor.linkedin) return "/linkedin-avatar.png";
  if (contributor.twitter) return "/twitter-avatar.png";
  return null;
}
function getLinkedInPhotoUrl(linkedin: string | undefined) {
  if (!linkedin) return null;
  const match = linkedin.match(/linkedin\.com\/in\/([a-zA-Z0-9-_]+)/);
  if (match) {
    return `https://www.linkedin.com/in/${match[1]}/overlay/photo/`;
  }
  return null;
}

function ContributorAvatar({ name, avatar }: { name: string; avatar?: string | null }) {
  const initials = getInitials(name);
  return (
    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2 overflow-hidden border-2 border-[#daed6e]" style={{background: 'linear-gradient(135deg, #daed6e 60%, #e1dcdc 100%)'}}>
      {avatar ? (
        <img src={avatar} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-2xl font-bold text-gray-800">{initials}</span>
      )}
    </div>
  );
}

const AdminContributionsPage = () => {
  const navigate = useNavigate();
  const [pendingContributions, setPendingContributions] = useState<Contribution[]>([]);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewError, setReviewError] = useState("");
  const [manualContributor, setManualContributor] = useState({
    name: '', linkedin: '', twitter: '', reddit: '', contributionType: '', avatar: ''
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualSuccess, setManualSuccess] = useState(false);
  const [manualError, setManualError] = useState("");
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editContributor, setEditContributor] = useState<Contributor | null>(null);
  const [editForm, setEditForm] = useState({ name: '', linkedin: '', twitter: '', reddit: '', contributionType: '', avatar: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  useEffect(() => {
    contributionService.getPendingContributions().then(setPendingContributions).catch(() => setReviewError("Failed to load contributions")).finally(() => setReviewLoading(false));
    contributionService.getContributors().then(setContributors);
  }, []);

  const handleAccept = async (id: string) => {
    await contributionService.acceptContribution(id, ADMIN_EMAIL);
    setPendingContributions(pendingContributions.filter(c => c.id !== id));
    // Refresh contributors list
    contributionService.getContributors().then(setContributors);
  };
  const handleReject = async (id: string) => {
    await contributionService.rejectContribution(id, ADMIN_EMAIL);
    setPendingContributions(pendingContributions.filter(c => c.id !== id));
  };
  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManualContributor({ ...manualContributor, [e.target.name]: e.target.value });
  };
  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setManualSubmitting(true);
    setManualError("");
    setManualSuccess(false);
    try {
      await contributionService.addContributor(manualContributor as any);
      setManualSuccess(true);
      setManualContributor({ name: '', linkedin: '', twitter: '', reddit: '', contributionType: '', avatar: '' });
      contributionService.getContributors().then(setContributors);
    } catch {
      setManualError("Failed to add contributor");
    } finally {
      setManualSubmitting(false);
    }
  };

  const openEditModal = (contributor: Contributor) => {
    setEditContributor(contributor);
    setEditForm({
      name: contributor.name || '',
      linkedin: contributor.linkedin || '',
      twitter: contributor.twitter || '',
      reddit: contributor.reddit || '',
      contributionType: contributor.contributionType || '',
      avatar: contributor.avatar || '',
    });
    setEditModalOpen(true);
  };
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editContributor) return;
    setEditLoading(true);
    await contributionService.updateContributor(editContributor.id, editForm);
    setEditModalOpen(false);
    setEditContributor(null);
    setEditLoading(false);
    contributionService.getContributors().then(setContributors);
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contributor?')) return;
    setDeleteLoading(id);
    await contributionService.deleteContributor(id);
    setDeleteLoading(null);
    contributionService.getContributors().then(setContributors);
  };

  // --- Admin Nav Bar ---
  const adminNav = (
    <div className="fixed w-full px-4 top-[46px] z-10">
      <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
        <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
        <div className="relative h-full flex items-center justify-between px-6">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/admin')}>
            <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
            <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/aAyuiOcvMq/admin')} className="font-['Space_Grotesk'] text-black">Back to Dashboard</Button>
            <div onClick={() => navigate('/signin')} className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"><img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" /></div>
          </div>
        </div>
      </div>
    </div>
  );

  // --- Contributors Marquee ---
  const contributorsMarquee = (
    <div className="w-full overflow-x-hidden py-8 bg-white rounded-xl mb-8 relative">
      <h2 className="text-2xl font-bold text-center mb-4">Contributors</h2>
      <div className="marquee flex items-center gap-8 animate-scroll-x" style={{animation: 'scroll-x 40s linear infinite'}}>
        {contributors.map((c, idx) => {
          let name = c.name?.trim() || '';
          if (!name) name = getRandomAnon();
          return (
            <div key={c.id || idx} className="flex flex-col items-center min-w-[120px] mx-2 relative group">
              <ContributorAvatar name={name} avatar={c.avatar} />
              <div className="font-semibold text-center text-sm max-w-[100px] truncate">{name}</div>
              <div className="absolute top-0 right-0 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="outline" className="px-2 py-1 text-xs" onClick={() => openEditModal(c)}>Edit</Button>
                <Button size="sm" variant="destructive" className="px-2 py-1 text-xs" onClick={() => handleDelete(c.id)} disabled={deleteLoading === c.id}>{deleteLoading === c.id ? 'Deleting...' : 'Delete'}</Button>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes scroll-x {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee { width: max-content; }
      `}</style>
      {/* Edit Contributor Modal */}
      {editModalOpen && editContributor && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-black" onClick={() => setEditModalOpen(false)}>&times;</button>
            <h3 className="text-xl font-bold mb-2">Edit Contributor</h3>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <input name="name" value={editForm.name} onChange={handleEditChange} placeholder="Name" className="w-full border rounded p-2" />
              <input name="contributionType" value={editForm.contributionType} onChange={handleEditChange} placeholder="Contribution Type" className="w-full border rounded p-2" />
              <div className="flex gap-2">
                <input name="linkedin" value={editForm.linkedin} onChange={handleEditChange} placeholder="LinkedIn" className="w-full border rounded p-2" />
                <input name="twitter" value={editForm.twitter} onChange={handleEditChange} placeholder="Twitter" className="w-full border rounded p-2" />
                <input name="reddit" value={editForm.reddit} onChange={handleEditChange} placeholder="Reddit" className="w-full border rounded p-2" />
              </div>
              <div>
                <label className="block text-sm font-medium">Avatar (optional)</label>
                <input type="file" accept="image/*" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setEditForm(prev => ({ ...prev, avatar: ev.target?.result as string }));
                    reader.readAsDataURL(file);
                  }
                }} />
                {editForm.avatar && <img src={editForm.avatar} alt="avatar preview" className="w-16 h-16 rounded-full mt-2" />}
              </div>
              <Button type="submit" className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Changes'}</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#e1dcdc75] pb-12 pt-[140px] px-4">
      {adminNav}
      <div className="max-w-2xl mx-auto">
        {contributorsMarquee}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Contribution Review & Quick Add</CardTitle>
          </CardHeader>
          <CardContent>
            <h3 className="font-semibold mb-2">Pending Contributions</h3>
            {reviewLoading ? <div>Loading...</div> : reviewError ? <div className="text-red-500">{reviewError}</div> : (
              <div className="space-y-4">
                {pendingContributions.length === 0 && <div className="text-gray-500">No pending contributions.</div>}
                {pendingContributions.map(c => (
                  <div key={c.id} className="border rounded p-3 bg-gray-50">
                    <div className="font-bold">{c.type}</div>
                    <div className="text-sm mb-1">{c.details}</div>
                    <div className="text-xs text-gray-500 mb-1">By: {c.contributorName || 'Anonymous'}</div>
                    <div className="flex gap-2 mb-1">
                      {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>}
                      {c.twitter && <a href={c.twitter} target="_blank" rel="noopener noreferrer">Twitter</a>}
                      {c.reddit && <a href={c.reddit} target="_blank" rel="noopener noreferrer">Reddit</a>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-200" onClick={() => handleAccept(c.id)}>Accept</Button>
                      <Button size="sm" className="bg-red-200" onClick={() => handleReject(c.id)}>Reject</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <hr className="my-6" />
            <h3 className="font-semibold mb-2">Manually Add Contributor</h3>
            <form onSubmit={handleManualSubmit} className="space-y-2">
              <input name="name" value={manualContributor.name} onChange={handleManualChange} placeholder="Name" className="border rounded p-2 w-full" />
              <input name="contributionType" value={manualContributor.contributionType} onChange={handleManualChange} placeholder="Contribution Type" className="border rounded p-2 w-full" />
              <div className="flex gap-2">
                <input name="linkedin" value={manualContributor.linkedin} onChange={handleManualChange} placeholder="LinkedIn" className="border rounded p-2 w-full" />
                <input name="twitter" value={manualContributor.twitter} onChange={handleManualChange} placeholder="Twitter" className="border rounded p-2 w-full" />
                <input name="reddit" value={manualContributor.reddit} onChange={handleManualChange} placeholder="Reddit" className="border rounded p-2 w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium">Avatar (optional)</label>
                <input type="file" accept="image/*" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setManualContributor(prev => ({ ...prev, avatar: ev.target?.result as string }));
                    reader.readAsDataURL(file);
                  }
                }} />
                {manualContributor.avatar && <img src={manualContributor.avatar} alt="avatar preview" className="w-16 h-16 rounded-full mt-2" />}
              </div>
              {manualError && <div className="text-red-500 text-xs">{manualError}</div>}
              {manualSuccess && <div className="text-green-600 text-xs">Contributor added!</div>}
              <Button type="submit" className="bg-[#daed6e] hover:bg-[#c5d85c] text-black" disabled={manualSubmitting}>
                {manualSubmitting ? 'Adding...' : 'Add Contributor'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminContributionsPage; 